import { ApiService } from '../services/api'
import { AppState } from '../stores/app-state'
import { Toast } from './toast'
import type { ProjectDocument, PDFFile } from '@/types'

export class PDFSelector {
  private container: HTMLElement
  private apiService: ApiService
  private appState: AppState
  private toastService: Toast
  private pdfs: ProjectDocument[] = []
  private isLoading = false

  constructor(
    container: HTMLElement,
    apiService: ApiService,
    appState: AppState,
    toastService: Toast
  ) {
    this.container = container
    this.apiService = apiService
    this.appState = appState
    this.toastService = toastService
    
    this.init()
  }

  private init(): void {
    this.pdfs = this.appState.getProjectDocuments()
    this.render()
    this.setupEventListeners()
  }

  private setupEventListeners(): void {
    this.appState.subscribe('projectDocuments', (docs: ProjectDocument[]) => {
      this.pdfs = docs
      this.render()
      
      const currentFile = this.appState.getCurrentFile()
      if (this.pdfs.length > 0 && !currentFile) {
        this.selectPDF(this.pdfs[0])
      }
    })

    this.appState.subscribe('currentFile', () => {
      this.render()
    })
  }

  private setLoading(loading: boolean): void {
    this.isLoading = loading
    this.render()
  }

  private async selectPDF(pdf: ProjectDocument): Promise<void> {
    try {
      this.setLoading(true)
      const documentInfo = await this.apiService.getDocumentInfo(pdf.id)
      const pdfFile: PDFFile = {
        id: documentInfo.id || documentInfo.document_id,
        project_id: documentInfo.project_id,
        filename: documentInfo.original_filename,
        pageCount: documentInfo.page_count || 1,
        dimensions: documentInfo.dimensions || { width: 0, height: 0 },
      }
      this.appState.setCurrentFile(pdfFile)
    } catch (error) {
      console.error('Failed to select PDF:', error)
      this.toastService.error('Failed to load PDF details')
    } finally {
      this.setLoading(false)
    }
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  private formatDate(isoString: string): string {
    return new Date(isoString).toLocaleDateString()
  }

  private async deletePDF(pdfId: string): Promise<void> {
    const pdf = this.pdfs.find(p => p.id === pdfId)
    if (!pdf) {
      console.error('PDF not found for deletion:', pdfId)
      return
    }

    console.log('🗑️ Starting PDF deletion process for:', pdf.original_filename)

    // Confirm deletion
    const confirmDelete = confirm(
      `Delete "${pdf.original_filename}"?\n\n` +
      `This will permanently remove the document and all its text selections from this project.\n\n` +
      `This action cannot be undone.`
    )

    if (!confirmDelete) {
      console.log('🚫 PDF deletion cancelled by user')
      return
    }

    console.log('✅ PDF deletion confirmed by user')

    try {
      this.setLoading(true)
      console.log('🗑️ Deleting PDF document:', pdf.original_filename)

      // Delete the document via API
      console.log('📡 Calling API to delete PDF:', pdfId)
      const result = await this.apiService.deletePDF(pdfId)
      console.log('✅ API deletion successful:', result)
      
      // Update local state IMMEDIATELY after successful API call
      console.log('📋 Updating local state - removing PDF from list')
      this.pdfs = this.pdfs.filter(p => p.id !== pdfId)
      console.log('📋 PDFs remaining:', this.pdfs.length)
      
      // Update app state
      console.log('📱 Updating app state with new PDF list')
      this.appState.setProjectDocuments(this.pdfs)
      
      // If this was the currently selected file, clear it
      const currentFile = this.appState.getCurrentFile()
      if (currentFile?.id === pdfId) {
        console.log('📄 Clearing currently selected file (was deleted)')
        this.appState.setCurrentFile(null)
        
        // If there are other PDFs, select the first one
        if (this.pdfs.length > 0) {
          console.log('📄 Selecting first remaining PDF')
          await this.selectPDF(this.pdfs[0])
        }
      }

      // Clear any selections that belonged to this PDF
      const currentSelections = this.appState.getSelections()
      const remainingSelections = currentSelections.filter(s => s.document_id !== pdfId)
      if (remainingSelections.length !== currentSelections.length) {
        console.log(`🧹 Removing ${currentSelections.length - remainingSelections.length} selections from deleted PDF`)
        this.appState.setSelections(remainingSelections)
      }

      // Force re-render to ensure UI updates
      console.log('🔄 Forcing component re-render')
      this.render()

      this.toastService.success('Document Deleted', `"${pdf.original_filename}" has been removed from the project`)
      console.log('✅ PDF deletion completed successfully')
      
    } catch (error) {
      console.error('❌ PDF deletion failed:', error)
      console.error('Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        response: (error as any)?.response?.data
      })
      this.toastService.error('Delete Failed', 'Could not delete the document. Please try again.')
    } finally {
      this.setLoading(false)
      console.log('🔄 PDF deletion process completed')
    }
  }

  private render(): void {
    const currentFile = this.appState.getCurrentFile()
    
    this.container.innerHTML = `
      <div class="pdf-selector">
        ${this.isLoading ? `
          <div class="pdf-selector-loading">
            <div class="spinner"></div>
            <p>Loading PDFs...</p>
          </div>
        ` : ''}
        
        ${this.pdfs.length === 0 && !this.isLoading ? `
          <div class="pdf-selector-empty">
            <div class="text-4xl mb-3">📄</div>
            <h3 class="text-lg font-medium text-gray-700 mb-2">No PDFs Available</h3>
            <p class="text-gray-500">Upload PDFs in the Document Management step first</p>
          </div>
        ` : ''}
        
        ${this.pdfs.length > 0 ? `
          <div class="pdf-selector-list">
            ${this.pdfs.map(pdf => `
              <div class="pdf-selector-item ${currentFile?.id === pdf.id ? 'selected' : ''}" data-pdf-id="${pdf.id}">
                <div class="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:border-blue-300 transition-all group relative">
                  <div class="text-xl">📄</div>
                  <div class="flex-1 cursor-pointer" data-action="select" data-pdf-id="${pdf.id}">
                    <h5 class="font-medium text-gray-900">${pdf.original_filename}</h5>
                    <div class="flex space-x-4 text-xs text-gray-500 mt-1">
                      <span>${this.formatFileSize(pdf.file_size)}</span>
                      <span>${pdf.page_count || 0} pages</span>
                      <span>${this.formatDate(pdf.upload_timestamp)}</span>
                    </div>
                  </div>
                  <button 
                    class="delete-pdf-btn opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-100 rounded text-red-600 hover:text-red-700 min-w-[24px] min-h-[24px] flex items-center justify-center" 
                    data-action="delete" 
                    data-pdf-id="${pdf.id}"
                    title="Delete ${pdf.original_filename}"
                  >
                    ×
                  </button>
                </div>
              </div>
            `).join('')}
          </div>
        ` : ''}
      </div>
    `

    this.attachEventListeners()
  }

  private attachEventListeners(): void {
    // Handle clicks on the entire container to distinguish between select and delete actions
    this.container.addEventListener('click', (event) => {
      const target = event.target as HTMLElement
      const button = target.closest('button[data-action]') as HTMLElement
      const selectArea = target.closest('[data-action="select"]') as HTMLElement
      
      if (button && button.dataset.action === 'delete') {
        event.stopPropagation()
        const pdfId = button.dataset.pdfId
        if (pdfId) {
          this.deletePDF(pdfId)
        }
      } else if (selectArea && selectArea.dataset.action === 'select') {
        const pdfId = selectArea.dataset.pdfId
        const pdf = this.pdfs.find(p => p.id === pdfId)
        if (pdf && this.appState.getCurrentFile()?.id !== pdf.id) {
          this.selectPDF(pdf)
        }
      }
    })
  }

  // Public methods
  public refresh(): void {
    // No longer needed as this component now listens to appState
  }
}